import { useEffect, useState, useCallback, useMemo } from "react";
import api, { fmtErr } from "@/shared/lib/api";
import FileUpload from "@/shared/components/FileUpload";
import { fmtIST } from "@/shared/lib/time";
import {
  Ruler, ImageIcon, CheckCircle2, Clock, Send,
  MessageSquareWarning, FileText, Sparkles, Layers, AlertTriangle, Package,
  ArrowLeft, ChevronRight, ChevronDown, FolderOpen, MessageSquare, Paperclip, ListTodo,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/shared/lib/auth";
import MaterialDeclarationPanel from "@/features/design/components/MaterialDeclarationPanel";

/* ────────────────────────────────────────────────────────────────────────────
   Designer Portal — Msg 903 (world-class, scales to many projects & line items)
   • Dashboard: "Things to do" (actionable line items across all projects) +
     "Your projects" (folder cards with name + code + counts).
   • Project view: master–detail — a compact, horizontal list of line items on
     the left, the focused workspace (brief, instructions, reference, upload) on
     the right. Stays clean no matter how many items.
   Privacy-restricted: project NAME + code only — never the client identity,
   the full BOQ, or pricing.
   ──────────────────────────────────────────────────────────────────────── */

const STATE_META = {
  awaiting:          { label: "To design",        dot: "bg-amber-400",   chip: "bg-amber-100 text-amber-800 border-amber-300",     icon: Clock },
  changes_requested: { label: "Changes requested", dot: "bg-rose-400",    chip: "bg-rose-100 text-rose-700 border-rose-300",        icon: MessageSquareWarning },
  needs_print_file:  { label: "Print file needed", dot: "bg-orange-400",  chip: "bg-orange-100 text-orange-700 border-orange-300",  icon: AlertTriangle },
  submitted:         { label: "Under review",      dot: "bg-blue-400",    chip: "bg-blue-100 text-blue-700 border-blue-300",        icon: Clock },
  approved:          { label: "Approved",          dot: "bg-emerald-500", chip: "bg-emerald-100 text-emerald-700 border-emerald-300", icon: CheckCircle2 },
  needs_materials:   { label: "Enter materials",   dot: "bg-indigo-500",  chip: "bg-indigo-100 text-indigo-700 border-indigo-300",  icon: Package },
};
const ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp,.svg,.ai,.cdr,.eps,.psd,.tif,.tiff,.indd,.dxf,.dwg,.zip";
const isImg = (u) => /\.(jpe?g|png|webp|gif|svg)(\?|$)/i.test(u || "");

export default function DesignerPortal() {
  const { user } = useAuth();
  const [scopes, setScopes] = useState(null);
  const [view, setView] = useState({ code: null, bid: null });

  const load = useCallback(() => {
    api.get("/my-designs")
      .then((r) => setScopes(r.data.scopes || []))
      .catch((e) => { toast.error(fmtErr(e.response?.data?.detail) || "Failed to load"); setScopes([]); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const projects = useMemo(() => {
    const map = new Map();
    (scopes || []).forEach((s) => {
      if (!map.has(s.project_code)) map.set(s.project_code, { code: s.project_code, name: s.project_name || s.project_code, walls: [] });
      map.get(s.project_code).walls.push(s);
    });
    return Array.from(map.values()).map((p) => ({
      ...p,
      todo: p.walls.filter((s) => s.actionable).length,
      review: p.walls.filter((s) => s.state === "submitted").length,
      done: p.walls.filter((s) => s.state === "approved").length,
    })).sort((a, b) => b.todo - a.todo || a.name.localeCompare(b.name));
  }, [scopes]);

  const active = view.code ? projects.find((p) => p.code === view.code) : null;

  const openLine = (code, bid) => setView({ code, bid });
  const openProject = (code) => {
    const p = projects.find((x) => x.code === code);
    const first = p?.walls.find((s) => s.actionable) || p?.walls[0];
    setView({ code, bid: first?.boq_id || null });
  };

  return (
    <div className="p-5 sm:p-8 max-w-6xl mx-auto" data-testid="designer-portal">
      {scopes === null ? (
        <div className="py-24 text-center text-zinc-400">Loading your studio…</div>
      ) : active ? (
        <ProjectView project={active} selectedBid={view.bid}
                     onBack={() => setView({ code: null, bid: null })} onDone={load} />
      ) : (
        <Dashboard projects={projects} scopes={scopes} userName={user?.name}
                   onOpenLine={openLine} onOpenProject={openProject} />
      )}
    </div>
  );
}

/* ── Dashboard: Things to do + Your projects ─────────────────────────── */
function Dashboard({ projects, scopes, userName, onOpenLine, onOpenProject }) {
  const todo = scopes.filter((s) => s.actionable);
  const review = scopes.filter((s) => s.state === "submitted").length;
  const done = scopes.filter((s) => s.state === "approved").length;

  return (
    <>
      <div className="mb-8">
        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-indigo-600 font-semibold">Your studio</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">
          {userName ? `Welcome back, ${userName.split(" ")[0]}` : "Your studio"}
        </h1>
        <div className="flex flex-wrap items-center gap-2 mt-4">
          <Stat label="To do" value={todo.length} tone="amber" />
          <Stat label="In review" value={review} tone="blue" />
          <Stat label="Approved" value={done} tone="emerald" />
          <Stat label="Projects" value={projects.length} tone="indigo" />
        </div>
      </div>

      {projects.length === 0 ? (
        <div className="py-20 text-center" data-testid="designer-empty">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-indigo-50 mb-4">
            <Sparkles className="w-7 h-7 text-indigo-400" strokeWidth={1.5} />
          </div>
          <div className="font-display text-xl font-bold">No projects assigned yet</div>
          <p className="text-zinc-500 mt-1 text-sm">When the CSM assigns you a wall, the project shows up here.</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_360px] gap-8 items-start">
          {/* Things to do */}
          <section data-testid="dp-things-to-do">
            <div className="flex items-center gap-2 mb-3">
              <ListTodo className="w-4 h-4 text-indigo-600" strokeWidth={1.5} />
              <h2 className="font-display text-lg font-bold tracking-tight">Things to do</h2>
              <span className="text-xs text-zinc-400">{todo.length}</span>
            </div>
            {todo.length === 0 ? (
              <div className="rounded-xl border border-dashed border-zinc-200 bg-white px-5 py-10 text-center">
                <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2" strokeWidth={1.5} />
                <div className="font-medium text-zinc-700">All caught up</div>
                <div className="text-sm text-zinc-400 mt-0.5">Nothing needs your attention right now.</div>
              </div>
            ) : (
              <div className="bg-white border border-zinc-200 rounded-xl divide-y divide-zinc-100 overflow-hidden">
                {todo.map((s) => <TodoRow key={`${s.project_code}-${s.boq_id}`} s={s} onClick={() => onOpenLine(s.project_code, s.boq_id)} />)}
              </div>
            )}
          </section>

          {/* Your projects */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <FolderOpen className="w-4 h-4 text-indigo-600" strokeWidth={1.5} />
              <h2 className="font-display text-lg font-bold tracking-tight">Your projects</h2>
              <span className="text-xs text-zinc-400">{projects.length}</span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-1 gap-3">
              {projects.map((p) => <ProjectFolderCard key={p.code} p={p} onClick={() => onOpenProject(p.code)} />)}
            </div>
          </section>
        </div>
      )}
    </>
  );
}

function TodoRow({ s, onClick }) {
  const meta = STATE_META[s.state] || STATE_META.awaiting;
  return (
    <button onClick={onClick} data-testid={`dp-todo-row-${s.boq_id}`}
            className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-indigo-50/40 transition-colors group">
      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${meta.dot}`} />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-zinc-900 truncate">
          {s.wall_label ? <span className="text-indigo-600 font-mono text-xs mr-1.5">{s.wall_label}</span> : null}{s.title}
          {s.linked_count > 1 && (
            <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-semibold whitespace-nowrap"
                  data-testid={`dp-linked-badge-${s.boq_id}`}>1 design × {s.linked_count}</span>
          )}
        </div>
        <div className="text-[11px] text-zinc-400 truncate flex items-center gap-1">
          <FolderOpen className="w-3 h-3" strokeWidth={1.5} /> {s.project_name}
        </div>
      </div>
      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border whitespace-nowrap ${meta.chip}`}>{meta.label}</span>
      <ChevronRight className="w-4 h-4 text-zinc-300 group-hover:text-indigo-500 flex-shrink-0 transition-colors" strokeWidth={1.5} />
    </button>
  );
}

function ProjectFolderCard({ p, onClick }) {
  return (
    <button onClick={onClick} data-testid={`dp-project-card-${p.code}`}
            className="group text-left bg-white border border-zinc-200 rounded-xl p-4 shadow-sm hover:shadow-md hover:border-indigo-300 transition-all">
      <div className="flex items-start gap-3">
        <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex-shrink-0">
          <FolderOpen className="w-5 h-5" strokeWidth={1.5} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold tracking-tight truncate">{p.name}</div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">{p.code}</div>
        </div>
        <ChevronRight className="w-4 h-4 text-zinc-300 group-hover:text-indigo-500 transition-colors" strokeWidth={1.5} />
      </div>
      <div className="flex flex-wrap gap-1.5 mt-3">
        {p.todo > 0 && <MiniPill tone="amber">{p.todo} to do</MiniPill>}
        {p.review > 0 && <MiniPill tone="blue">{p.review} in review</MiniPill>}
        {p.done > 0 && <MiniPill tone="emerald">{p.done} approved</MiniPill>}
        <MiniPill tone="zinc">{p.walls.length} wall{p.walls.length !== 1 ? "s" : ""}</MiniPill>
      </div>
    </button>
  );
}

/* ── Project view: groups (left) → that group's walls (right) ────────── */
function buildGroups(walls) {
  const map = new Map();
  for (const w of walls) { const g = w.group || ""; if (!map.has(g)) map.set(g, []); map.get(g).push(w); }
  const entries = [...map.entries()].sort((a, b) =>
    a[0] === "" ? 1 : b[0] === "" ? -1 : a[0].localeCompare(b[0]));
  return entries.map(([name, ws]) => ({
    key: name || "__ungrouped__", name, walls: ws,
    todo: ws.filter((s) => s.actionable).length,
  }));
}

function ProjectView({ project, selectedBid, onBack, onDone }) {
  const groups = useMemo(() => buildGroups(project.walls), [project.walls]);
  const noGroups = groups.length === 1 && groups[0].name === "";
  const initial = useMemo(() => {
    const sel = project.walls.find((w) => w.boq_id === selectedBid);
    if (sel) return sel.group || "__ungrouped__";
    return (groups.find((g) => g.todo > 0) || groups[0])?.key;
  }, [project.walls, selectedBid, groups]);
  const [gkey, setGkey] = useState(initial);
  const active = groups.find((g) => g.key === gkey) || groups[0];

  return (
    <div data-testid={`dp-wall-detail-view-${project.code}`}>
      <button onClick={onBack} data-testid="dp-back-btn"
              className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-indigo-600 transition-colors mb-4">
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> All projects
      </button>
      <div className="mb-6">
        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-indigo-600 font-semibold">{project.code}</div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1">{project.name}</h1>
        <p className="text-zinc-500 mt-1 text-sm">
          {noGroups ? `${project.walls.length} line item${project.walls.length !== 1 ? "s" : ""} assigned to you.`
            : `Pick a group on the left — its walls open on the right to design.`}
        </p>
      </div>

      <div className="grid lg:grid-cols-[minmax(240px,320px)_1fr] gap-6 items-start">
        <GroupRail groups={groups} active={active?.key} onSelect={setGkey} noGroups={noGroups} />
        <div className="space-y-4" data-testid={`dp-group-walls-${active?.key}`}>
          {!noGroups && (
            <div className="flex items-center gap-2 px-1">
              <Layers className="w-4 h-4 text-indigo-600" strokeWidth={1.5} />
              <h2 className="font-display text-lg font-bold tracking-tight">{active?.name || "Ungrouped"}</h2>
              <span className="text-xs text-zinc-400">· {active?.walls.length} wall{active?.walls.length !== 1 ? "s" : ""}</span>
            </div>
          )}
          {(active?.walls || []).map((s) => (
            <ScopeDetail key={s.boq_id} scope={s} onDone={onDone} />
          ))}
        </div>
      </div>
    </div>
  );
}

function GroupRail({ groups, active, onSelect, noGroups }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-xl divide-y divide-zinc-100 overflow-hidden lg:sticky lg:top-20" data-testid="dp-group-rail">
      {groups.map((g) => (
        <button key={g.key} onClick={() => onSelect(g.key)} data-testid={`dp-group-row-${g.key}`}
                className={`w-full flex items-center gap-3 px-3.5 py-3 text-left transition-colors ${g.key === active ? "bg-indigo-50/70" : "hover:bg-zinc-50"}`}>
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${g.name ? "bg-indigo-50 text-indigo-600" : "bg-zinc-100 text-zinc-400"}`}>
            <Layers className="w-4 h-4" strokeWidth={1.5} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-zinc-900 truncate">{noGroups ? "All walls" : (g.name || "Ungrouped")}</div>
            <div className="text-[11px] text-zinc-500 mt-0.5">
              {g.walls.length} wall{g.walls.length !== 1 ? "s" : ""}
              {g.todo > 0 && <span className="text-amber-600"> · {g.todo} to do</span>}
            </div>
          </div>
          <ChevronRight className={`w-4 h-4 flex-shrink-0 ${g.key === active ? "text-indigo-500" : "text-zinc-300"}`} strokeWidth={1.5} />
        </button>
      ))}
    </div>
  );
}

/* ── The focused workspace for one line item ─────────────────────────── */
function ScopeDetail({ scope, onDone }) {
  const meta = STATE_META[scope.state] || STATE_META.awaiting;
  const Icon = meta.icon;
  const [url, setUrl] = useState("");
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [fileBusy, setFileBusy] = useState(false);
  const [open, setOpen] = useState(!!scope.actionable);  // collapse by default; auto-open actionable

  async function submit() {
    if (fileBusy) { toast.error("Wait — your design file is still uploading."); return; }
    if (!url) { toast.error("Attach your design file first"); return; }
    setSaving(true);
    try {
      const r = await api.post(`/my-designs/${scope.project_code}/${scope.boq_id}/submit`, {
        file_url: url, file_name: name || "design.file",
        file_type: (name.split(".").pop() || "").toLowerCase(), note: note.trim(),
      });
      toast.success(r.data.mode === "print_file"
        ? "Print-ready file submitted to production."
        : "Design submitted! It's now with the reviewer.");
      setUrl(""); setName(""); setNote(""); onDone();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Submit failed"); }
    finally { setSaving(false); }
  }

  const dim = (scope.height && scope.width) ? `${scope.height}″ × ${scope.width}″` : null;
  const actual = (scope.site_actual_height && scope.site_actual_width)
    ? `${scope.site_actual_height} × ${scope.site_actual_width} ${scope.site_actual_unit || ""}` : null;
  const instructions = scope.instructions?.length > 0
    ? scope.instructions
    : (scope.instruction ? [{ id: "single", text: scope.instruction }] : []);
  const showInstructions = (scope.state === "changes_requested" || scope.state === "needs_print_file") && instructions.length > 0;

  return (
    <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden" data-testid={`designer-scope-${scope.boq_id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`designer-scope-toggle-${scope.boq_id}`}
              className="w-full flex items-center justify-between gap-3 p-4 sm:p-5 text-left hover:bg-zinc-50/60 transition-colors">
        <div className="min-w-0 flex items-center gap-3">
          <ChevronDown className={`w-4 h-4 text-zinc-400 shrink-0 transition-transform ${open ? "" : "-rotate-90"}`} strokeWidth={1.5} />
          <div className="min-w-0">
            {scope.wall_label && <div className="font-mono text-[11px] uppercase tracking-widest text-indigo-500">{scope.wall_label}</div>}
            <h3 className="font-display text-base sm:text-lg font-bold tracking-tight truncate">{scope.title}</h3>
            {scope.linked_count > 1 && (
              <div className="text-[11px] text-indigo-600 font-semibold mt-0.5" data-testid={`dp-linked-note-${scope.boq_id}`}>
                Shared design — this one file covers {scope.linked_count} linked items
              </div>
            )}
          </div>
        </div>
        <span className={`inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full text-[11px] font-medium border whitespace-nowrap ${meta.chip}`}
              data-testid={`designer-scope-status-${scope.boq_id}`}>
          <Icon className="w-3.5 h-3.5" strokeWidth={1.5} /> {meta.label}
        </span>
      </button>
      {open && (<>
      <div className="p-5 sm:p-6 border-t border-zinc-100">

        {scope.designer_brief ? (
          <div className="mt-4 bg-indigo-50/60 border border-indigo-100 rounded-xl p-4" data-testid={`dp-csm-brief-${scope.boq_id}`}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-indigo-600 mb-1 flex items-center gap-1">
              <MessageSquare className="w-3 h-3" strokeWidth={1.5} /> Brief from the CSM
            </div>
            <p className="text-sm text-zinc-800 whitespace-pre-line">{scope.designer_brief}</p>
          </div>
        ) : scope.brief && scope.brief !== scope.scope_description ? (
          <div className="mt-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mb-1">Design brief</div>
            <p className="text-sm text-zinc-700 whitespace-pre-line">{scope.brief}</p>
          </div>
        ) : null}

        {scope.scope_description && (
          <div className="mt-3 bg-amber-50/50 border border-amber-100 rounded-xl p-4" data-testid={`dp-scope-desc-${scope.boq_id}`}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-amber-700 mb-1">
              Scope description · {scope.title}
            </div>
            <p className="text-sm text-zinc-700 whitespace-pre-line leading-relaxed">{scope.scope_description}</p>
          </div>
        )}

        <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-4 text-xs text-zinc-600">
          {dim && <Spec icon={Ruler} label="Size" value={dim} />}
          {actual && <Spec icon={Ruler} label="Site actual" value={actual} highlight />}
          {scope.quantity != null && <Spec icon={Layers} label="Qty" value={`${scope.quantity} ${scope.unit || ""}`} />}
          {scope.surface_type && <Spec icon={Layers} label="Surface" value={scope.surface_type} />}
        </div>

        {scope.site_photos?.length > 0 && (
          <div className="mt-4">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mb-1.5 flex items-center gap-1">
              <ImageIcon className="w-3 h-3" strokeWidth={1.5} /> Site reference
            </div>
            <div className="flex flex-wrap gap-2">
              {scope.site_photos.map((p, i) => (
                <a key={i} href={p} target="_blank" rel="noreferrer" data-testid={`dp-site-photo-${scope.boq_id}-${i}`}
                   className="block w-20 h-16 rounded-lg overflow-hidden border border-zinc-200 hover:ring-2 hover:ring-indigo-300 transition">
                  <img src={p} alt="site" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                </a>
              ))}
            </div>
          </div>
        )}
      </div>

      {showInstructions && (
        <div className="px-5 sm:px-6 py-4 bg-rose-50/50 border-b border-rose-100" data-testid={`designer-instruction-${scope.boq_id}`}>
          <div className="text-[10px] font-mono uppercase tracking-widest text-rose-600 mb-2 flex items-center gap-1">
            <MessageSquareWarning className="w-3 h-3" strokeWidth={1.5} /> Instructions for your revision
          </div>
          <div className="space-y-2">
            {instructions.map((ins, i) => (
              <div key={ins.id || i} className="bg-white border border-rose-100 rounded-lg p-3">
                {instructions.length > 1 && <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 mb-1">#{i + 1}</div>}
                <p className="text-sm text-zinc-900 whitespace-pre-line">{ins.text}</p>
                {ins.file_url && (
                  <a href={ins.file_url} target="_blank" rel="noreferrer"
                     className="mt-2 inline-flex items-center gap-1.5 text-xs text-rose-600 hover:underline"
                     data-testid={`designer-instruction-file-${scope.boq_id}-${i}`}>
                    <Paperclip className="w-3.5 h-3.5" strokeWidth={1.5} /> {ins.file_name || "reference from CSM"}
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {scope.submitted && (
        <div className="px-5 sm:px-6 py-4 flex items-center gap-3 border-b border-zinc-100" data-testid={`designer-last-submission-${scope.boq_id}`}>
          <a href={scope.submitted.file_url} target="_blank" rel="noreferrer"
             className="w-16 h-16 rounded-lg bg-zinc-50 border border-zinc-200 overflow-hidden flex items-center justify-center flex-shrink-0 hover:ring-2 hover:ring-indigo-300 transition">
            {isImg(scope.submitted.file_url)
              ? <img src={scope.submitted.file_url} alt="your design" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
              : <FileText className="w-6 h-6 text-zinc-400" strokeWidth={1.5} />}
          </a>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">Your last submission</div>
            <a href={scope.submitted.file_url} target="_blank" rel="noreferrer"
               className="text-indigo-700 hover:underline truncate block font-medium text-sm" data-testid={`designer-submitted-${scope.boq_id}`}>
              V{scope.submitted.version_number} · {scope.submitted.file_name}
            </a>
            <div className="text-[11px] text-zinc-400">Submitted {fmtIST(scope.submitted.submitted_at)}</div>
          </div>
        </div>
      )}

      <div className="p-5 sm:p-6">
        {scope.state === "needs_materials" ? (
          <div className="flex items-center gap-2 text-sm text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-3"
               data-testid={`dp-materials-prompt-${scope.boq_id}`}>
            <Package className="w-4 h-4" strokeWidth={1.5} /> Design approved — please enter the material requirements below so Procurement can begin.
          </div>
        ) : scope.actionable ? (
          <div className="space-y-3">
            <div className="border-2 border-dashed border-zinc-200 rounded-xl p-1 hover:border-indigo-400 hover:bg-indigo-50/30 transition-colors"
                 data-testid={`dp-design-upload-zone-${scope.boq_id}`}>
              <FileUpload value={url} onChange={(u) => { setUrl(u); setName(u ? (u.split("/").pop() || "design.file") : ""); }}
                          onBusyChange={setFileBusy}
                          label="Upload your design" accept={ACCEPT} testid={`designer-upload-${scope.boq_id}`} />
            </div>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note about this design" rows={2}
                      className="w-full text-sm rounded-lg border border-zinc-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300"
                      data-testid={`designer-note-${scope.boq_id}`} />
            <button onClick={submit} disabled={saving || fileBusy || !url} data-testid={`dp-submit-design-button-${scope.boq_id}`}
                    className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-xl bg-indigo-600 text-white font-medium shadow-sm hover:bg-indigo-700 transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0">
              <Send className="w-4 h-4" strokeWidth={1.5} /> {fileBusy ? "File uploading…" : saving ? "Submitting…" : "Submit design"}
            </button>
          </div>
        ) : scope.state === "approved" ? (
          <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3">
            <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} /> Approved and handed to production. Nothing more to do here.
          </div>
        ) : (
          <div className="flex items-center gap-2 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
            <Clock className="w-4 h-4" strokeWidth={1.5} /> Submitted — under review. You'll be notified if anything is needed.
          </div>
        )}
      </div>

      {scope.material_unlocked && <MaterialDeclarationPanel scope={scope} onDone={onDone} />}
      </>)}
    </div>
  );
}

function Stat({ label, value, tone }) {
  const cls = {
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    blue: "bg-blue-50 text-blue-800 border-blue-200",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    indigo: "bg-indigo-50 text-indigo-800 border-indigo-200",
  }[tone];
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-xs font-medium border ${cls}`}>
      <span className="font-bold tabular-nums">{value}</span> {label}
    </span>
  );
}

function MiniPill({ tone, children }) {
  const cls = {
    amber: "bg-amber-100 text-amber-800",
    blue: "bg-blue-100 text-blue-700",
    emerald: "bg-emerald-100 text-emerald-700",
    zinc: "bg-zinc-100 text-zinc-600",
  }[tone];
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${cls}`}>{children}</span>;
}

function Spec({ icon: Icon, label, value, highlight }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <Icon className="w-3.5 h-3.5 text-zinc-400" strokeWidth={1.5} />
      <span className="text-zinc-400">{label}:</span>
      <span className={highlight ? "font-semibold text-emerald-700" : "font-medium text-zinc-700"}>{value}</span>
    </span>
  );
}
